import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ArrowUpDown, Users, AlertTriangle, TrendingUp, TrendingDown,
  Plus, ChevronRight, Activity, Loader2, Calendar, Filter, Zap, FileDown,
} from 'lucide-react';
import { toast } from 'sonner';

const TEST_LABELS = {
  SJ: 'Squat Jump', CMJ: 'Counter Movement Jump', CMJ_RIGHT_LEFT: 'CMJ Right/Left',
  LOADED_CMJ: 'Loaded CMJ', DROP_JUMP: 'Drop Jump',
  // iter_119
  CMJ_FREE_ARMS: 'CMJ Free Arms', DRIFT_2D: 'Drift 2D', STIFFNESS: 'Stiffness',
};

const ALERT_STYLES = {
  PERFORMANCE_DROP: { color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30' },
  ASYMMETRY: { color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30' },
  RSI_LOW: { color: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/30' },
  MISSING_TEST: { color: 'text-zinc-400', bg: 'bg-zinc-500/10', border: 'border-zinc-500/30' },
};

export default function StaffJumpPage() {
  const { api } = useAuth();
  const { language } = useLanguage();
  const t = (en, it) => language === 'IT' ? it : en;

  const [sessions, setSessions] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterTeam, setFilterTeam] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [exporting, setExporting] = useState(false);

  const handleExportTeamPdf = async () => {
    let teamId = filterTeam !== 'all' ? filterTeam : (teams[0]?.id);
    if (!teamId) {
      toast.error(t('Select a team to export', 'Seleziona una squadra da esportare'));
      return;
    }
    setExporting(true);
    try {
      const body = { team_id: teamId };
      if (filterType !== 'all') body.test_type = filterType;
      const res = await api.post('/jump-reports/team', body, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `jump_report_team_${teamId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('Report downloaded', 'Report scaricato'));
    } catch {
      toast.error(t('Export failed', 'Esportazione non riuscita'));
    }
    setExporting(false);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterTeam !== 'all') params.team_id = filterTeam;
      if (filterType !== 'all') params.test_type = filterType;
      const [sessR, teamsR] = await Promise.all([
        api.get('/jump/sessions', { params }),
        api.get('/teams'),
      ]);
      setSessions(sessR.data);
      setTeams(teamsR.data);

      // Load alerts for the first team (or selected)
      const teamId = filterTeam !== 'all' ? filterTeam : teamsR.data[0]?.id;
      if (teamId) {
        const alertsR = await api.get(`/jump/team/${teamId}/alerts`);
        setAlerts(alertsR.data);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, [api, filterTeam, filterType]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const completedSessions = sessions.filter(s => s.status === 'COMPLETED');
  const latestSession = completedSessions[0];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="staff-jump-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-100 flex items-center gap-2">
            <Zap className="w-6 h-6 text-cyan-400" />
            {t('Jump Testing', 'Test di Salto')}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {t('Manage jump performance testing', 'Gestisci i test di salto')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={handleExportTeamPdf}
            disabled={exporting || teams.length === 0}
            variant="outline"
            className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10"
            data-testid="staff-jump-export-pdf-btn"
          >
            {exporting
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <FileDown className="w-4 h-4 mr-2" />}
            {t('Export PDF', 'Esporta PDF')}
          </Button>
          <Link to="/staff/jump/group-test">
            <Button className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="new-group-test-btn">
              <Plus className="w-4 h-4 mr-2" /> {t('Group Test', 'Test di Gruppo')}
            </Button>
          </Link>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4 text-center">
            <Calendar className="w-5 h-5 text-cyan-400 mx-auto mb-1" />
            <div className="text-2xl font-bold text-zinc-100" data-testid="stat-sessions">{sessions.length}</div>
            <div className="text-xs text-zinc-500 uppercase">{t('Sessions', 'Sessioni')}</div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4 text-center">
            <Users className="w-5 h-5 text-blue-400 mx-auto mb-1" />
            <div className="text-2xl font-bold text-zinc-100" data-testid="stat-athletes">
              {latestSession?.athletes_tested || 0}
            </div>
            <div className="text-xs text-zinc-500 uppercase">{t('Last Test Athletes', 'Atleti Ultimo Test')}</div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4 text-center">
            <TrendingUp className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
            <div className="text-2xl font-bold text-zinc-100" data-testid="stat-best">
              {latestSession?.summary?.best_height || latestSession?.summary?.best_rsi || '—'}
            </div>
            <div className="text-xs text-zinc-500 uppercase">{t('Best (Last Test)', 'Migliore (Ultimo)')}</div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4 text-center">
            <AlertTriangle className="w-5 h-5 text-red-400 mx-auto mb-1" />
            <div className="text-2xl font-bold text-zinc-100" data-testid="stat-alerts">{alerts.length}</div>
            <div className="text-xs text-zinc-500 uppercase">{t('Active Alerts', 'Allerte')}</div>
          </CardContent>
        </Card>
      </div>

      {/* Alerts Panel */}
      {alerts.length > 0 && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-zinc-100 text-base flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-400" /> {t('Alerts', 'Allerte')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {alerts.slice(0, 8).map((a, i) => {
              const style = ALERT_STYLES[a.type] || ALERT_STYLES.MISSING_TEST;
              return (
                <div key={i} className={`flex items-center justify-between p-3 rounded-sm border ${style.bg} ${style.border}`}
                  data-testid={`alert-${i}`}>
                  <div className="flex items-center gap-2">
                    <AlertTriangle className={`w-4 h-4 ${style.color}`} />
                    <span className="text-sm text-zinc-200">{a.athlete_name}</span>
                    <span className={`text-xs ${style.color}`}>
                      {a.type === 'PERFORMANCE_DROP' && `${t('Drop', 'Calo')} ${a.drop_pct}%`}
                      {a.type === 'ASYMMETRY' && `${t('Asymmetry', 'Asimmetria')} ${a.asymmetry_pct}%`}
                      {a.type === 'RSI_LOW' && `RSI ${a.rsi}`}
                      {a.type === 'MISSING_TEST' && t('No recent test', 'Nessun test recente')}
                    </span>
                  </div>
                  {a.test_type && <span className="text-xs text-zinc-500">{TEST_LABELS[a.test_type] || a.test_type}</span>}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3">
        <Filter className="w-4 h-4 text-zinc-500" />
        <Select value={filterTeam} onValueChange={setFilterTeam}>
          <SelectTrigger className="w-40 bg-zinc-900 border-zinc-700 text-zinc-200" data-testid="filter-team">
            <SelectValue placeholder={t('All Teams', 'Tutti')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('All Teams', 'Tutti')}</SelectItem>
            {teams.map(tm => <SelectItem key={tm.id} value={tm.id}>{tm.code}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-48 bg-zinc-900 border-zinc-700 text-zinc-200" data-testid="filter-type">
            <SelectValue placeholder={t('All Types', 'Tutti i tipi')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('All Types', 'Tutti i tipi')}</SelectItem>
            {Object.entries(TEST_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Session List */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-zinc-100 text-base">{t('Sessions', 'Sessioni')}</CardTitle>
          <CardDescription className="text-zinc-500">{t('Recent jump testing sessions', 'Sessioni recenti')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {sessions.length === 0 ? (
            <div className="text-center py-8 text-zinc-500">{t('No sessions found', 'Nessuna sessione trovata')}</div>
          ) : (
            sessions.map(s => (
              <Link key={s.id} to={`/staff/jump/session/${s.id}`}
                className="flex items-center justify-between p-3 bg-zinc-950/50 rounded-sm border border-zinc-800 hover:border-zinc-700 transition-colors"
                data-testid={`session-row-${s.id}`}>
                <div className="flex items-center gap-3">
                  <div className={`p-1.5 rounded-sm ${s.status === 'COMPLETED' ? 'bg-emerald-500/10' : 'bg-amber-500/10'}`}>
                    <Activity className={`w-4 h-4 ${s.status === 'COMPLETED' ? 'text-emerald-400' : 'text-amber-400'}`} />
                  </div>
                  <div>
                    <div className="font-medium text-zinc-100 text-sm">
                      {TEST_LABELS[s.test_type] || s.test_type}
                      {s.team_code && <span className="text-zinc-500 ml-2 text-xs">{s.team_code}</span>}
                    </div>
                    <div className="text-xs text-zinc-500 mt-0.5">
                      {s.date} • {s.athletes_tested} {t('athletes', 'atleti')} • {s.result_count} {t('results', 'risultati')}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {s.summary?.team_avg_height && (
                    <span className="text-xs text-zinc-400">
                      {t('Avg', 'Med')}: <span className="text-zinc-200 font-medium">{s.summary.team_avg_height}cm</span>
                    </span>
                  )}
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                    s.status === 'COMPLETED' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'
                  }`}>{s.status}</span>
                  <ChevronRight className="w-4 h-4 text-zinc-600" />
                </div>
              </Link>
            ))
          )}
        </CardContent>
      </Card>

      {/* Latest Session Summary */}
      {latestSession?.summary && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-zinc-100 text-base">
              {t('Latest Session Summary', 'Riepilogo Ultimo Test')} — {TEST_LABELS[latestSession.test_type]}
            </CardTitle>
            <CardDescription className="text-zinc-500">{latestSession.date} • {latestSession.team_code}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 mb-4">
              {latestSession.summary.team_avg_height != null && (
                <div className="text-center p-3 bg-zinc-950/50 rounded-sm border border-zinc-800">
                  <ArrowUpDown className="w-4 h-4 text-blue-400 mx-auto mb-1" />
                  <div className="text-lg font-bold text-zinc-100">{latestSession.summary.team_avg_height} cm</div>
                  <div className="text-xs text-zinc-500">{t('Team Avg', 'Media Squadra')}</div>
                </div>
              )}
              {latestSession.summary.best_height != null && (
                <div className="text-center p-3 bg-zinc-950/50 rounded-sm border border-zinc-800">
                  <TrendingUp className="w-4 h-4 text-emerald-400 mx-auto mb-1" />
                  <div className="text-lg font-bold text-zinc-100">{latestSession.summary.best_height} cm</div>
                  <div className="text-xs text-zinc-500">{t('Best', 'Migliore')}</div>
                </div>
              )}
              {latestSession.summary.lowest_height != null && (
                <div className="text-center p-3 bg-zinc-950/50 rounded-sm border border-zinc-800">
                  <TrendingDown className="w-4 h-4 text-red-400 mx-auto mb-1" />
                  <div className="text-lg font-bold text-zinc-100">{latestSession.summary.lowest_height} cm</div>
                  <div className="text-xs text-zinc-500">{t('Lowest', 'Minore')}</div>
                </div>
              )}
            </div>
            {/* Athlete rankings */}
            <div className="space-y-1.5">
              {Object.values(latestSession.summary.athlete_summaries || {})
                .sort((a, b) => (b.height_cm || b.rsi || 0) - (a.height_cm || a.rsi || 0))
                .map((as, i) => (
                  <div key={as.athlete_id} className="flex items-center justify-between p-2 rounded-sm hover:bg-zinc-800/30"
                    data-testid={`ranking-${i}`}>
                    <div className="flex items-center gap-2">
                      <span className={`w-5 h-5 flex items-center justify-center rounded-full text-xs font-bold ${
                        i === 0 ? 'bg-amber-500/20 text-amber-400' : 'bg-zinc-800 text-zinc-500'
                      }`}>{i + 1}</span>
                      <span className="text-sm text-zinc-200">{as.athlete_name || as.athlete_id.slice(0, 8)}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      {as.height_cm != null && <span className="text-zinc-100 font-medium">{as.height_cm} cm</span>}
                      {as.rsi != null && <span className="text-cyan-400 font-medium">RSI {as.rsi}</span>}
                      {as.right_cm != null && (
                        <span className="text-zinc-400 text-xs">R:{as.right_cm} L:{as.left_cm}
                          <span className={`ml-1 ${as.asymmetry_pct > 10 ? 'text-red-400' : 'text-emerald-400'}`}>
                            ({as.asymmetry_pct}%)
                          </span>
                        </span>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
